import { Controller, type Control } from 'react-hook-form';
import { useTranslation } from '@/lib/i18n';
import { useUserGradesQuery } from '@/features/users/grades/hooks/useUserGradesQuery';
import type { ProductFormValues } from '../schemas/productSchema';

/**
 * QA-574 — '특정회원등급'에 비회원을 함께 고르는 예약 값. 등급 id 목록에 그대로 넣어 보내고, BE(catalog·order)가 비회원 판정에 쓴다
 * (BO-TO-BE-2026-10-27-product-grade-guest-qa574).
 */
export const GUEST_GRADE_ID = 'GUEST';

/** 등급 목록 한 번에 받는 크기 — 회원 등급은 수십 개 이하(알림 발송·사업자 승인과 같은 값). */
const GRADE_FETCH_SIZE = 100;

interface MemberGradePickerProps {
  control: Control<ProductFormValues>;
  name: 'purchaseGradeIds' | 'accessGradeIds' | 'benefitExclusionGradeIds' | 'mileageGradeIds' | 'mileageUseExclusionGradeIds';
  label: string;
  testId: string;
  /** QA-574 — 등급 앞에 [비회원] 체크를 둔다(구매·접근·혜택 제외). */
  includeGuest?: boolean | undefined;
}

/**
 * 상품 구매·접근 권한 '특정회원등급'(be-cat V132)의 허용 등급 다중 선택. 값은 user-service grade_id.
 * 미사용 등급도 이미 골라 둔 값이 사라지지 않게 목록에 남기되 표시만 구분한다.
 */
export const MemberGradePicker = ({ control, name, label, testId, includeGuest = false }: MemberGradePickerProps) => {
  const { t } = useTranslation('products');
  const gradesQuery = useUserGradesQuery({ page: 1, size: GRADE_FETCH_SIZE });
  const grades = [...(gradesQuery.data?.content ?? [])].sort((a, b) => a.priority - b.priority);

  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => {
        const selected = new Set(field.value ?? []);
        const toggle = (gradeId: string, on: boolean) => {
          const next = new Set(selected);
          if (on) next.add(gradeId);
          else next.delete(gradeId);
          field.onChange([...next]);
        };
        return (
          <fieldset className="flex flex-col gap-2" data-testid={testId}>
            <legend className="mb-1 text-[13px] font-medium" style={{ color: 'var(--content-default)' }}>
              {label}
            </legend>
            {gradesQuery.isError ? (
              <p className="text-[12px]" style={{ color: 'var(--status-danger-fg)' }}>
                {t('products.fields.gradesLoadError', '회원 등급을 불러오지 못했습니다.')}
              </p>
            ) : gradesQuery.isSuccess && grades.length === 0 ? (
              <p className="text-[12px]" style={{ color: 'var(--content-muted)' }}>
                {t('products.fields.gradesEmpty', '등록된 회원 등급이 없습니다.')}
              </p>
            ) : (
              <div className="flex flex-wrap gap-x-4 gap-y-2">
                {includeGuest ? (
                  <label className="inline-flex items-center gap-2 text-[14px]" style={{ color: 'var(--content-default)' }}>
                    <input
                      type="checkbox"
                      checked={selected.has(GUEST_GRADE_ID)}
                      onChange={(e) => toggle(GUEST_GRADE_ID, e.target.checked)}
                      aria-label={t('products.fields.gradeGuest', '비회원')}
                      data-testid={`${testId}-guest`}
                    />
                    {t('products.fields.gradeGuest', '비회원')}
                  </label>
                ) : null}
                {grades.map((g) => (
                  <label
                    key={g.gradeId}
                    className="inline-flex items-center gap-2 text-[14px]"
                    style={{ color: g.active ? 'var(--content-default)' : 'var(--content-muted)' }}
                  >
                    <input
                      type="checkbox"
                      checked={selected.has(g.gradeId)}
                      onChange={(e) => toggle(g.gradeId, e.target.checked)}
                      aria-label={g.gradeName}
                    />
                    {g.gradeName}
                    {g.active ? null : ` ${t('products.fields.gradeInactive', '(미사용)')}`}
                  </label>
                ))}
              </div>
            )}
            {fieldState.error?.message ? (
              <p className="text-[12px]" role="alert" style={{ color: 'var(--status-danger-fg)' }}>
                {t(fieldState.error.message, { defaultValue: fieldState.error.message })}
              </p>
            ) : null}
          </fieldset>
        );
      }}
    />
  );
};
